import type { ReactNode } from 'react'

/**
 * Charts built from published study numbers — not generative imagery.
 *
 * OkCupid / Rudder (2009, OkTrends "Your Looks and Your Inbox"):
 * - Women rate ~80% of men below "medium" attractiveness.
 * - Men rate women more evenly (roughly normal; majority around medium).
 * - About two-thirds of men's messages go to the best-looking third of women.
 *
 * Bruch & Newman, Science Advances (2018):
 * - In large U.S. online dating markets, both men and women tend to pursue
 *   partners about 25% more desirable than themselves (by the study's measure).
 */

export type ChartId = 'okcupid-below-medium' | 'okcupid-message-share' | 'bruch-aspirational'

export type StudyChartProps = {
  id: ChartId
}

export const StudyChart = (props: StudyChartProps) => {
  const { id } = props

  if (id === 'okcupid-below-medium') return <BelowMediumChart />
  if (id === 'okcupid-message-share') return <MessageShareChart />
  return <AspirationalChart />
}

const ChartShell = (props: {
  title: string
  source: string
  children: ReactNode
  note?: string
}) => {
  const { title, source, children, note } = props
  return (
    <figure className="study-chart">
      <figcaption className="study-chart__title">{title}</figcaption>
      <div className="study-chart__canvas">{children}</div>
      {note ? <p className="study-chart__note">{note}</p> : null}
      <p className="study-chart__source">{source}</p>
    </figure>
  )
}

/**
 * OkCupid headline figure: women rate ~80% of men below medium.
 * Men’s ratings of women were described as roughly balanced (majority medium).
 */
const BelowMediumChart = () => {
  const rows = [
    {
      label: 'Women rating men\nbelow “medium”',
      value: 80,
      color: 'var(--paper-flag)',
      display: '80%',
    },
    {
      label: 'Even split (reference)',
      value: 50,
      color: 'var(--gold-deep)',
      display: '50%',
      muted: true,
    },
  ]

  return (
    <ChartShell
      title="How harsh were attractiveness votes?"
      source="Source: OkCupid / Christian Rudder, “Your Looks and Your Inbox” (OkTrends, 2009) — women rated “an incredible 80% of guys as worse-looking than medium.” Men’s ratings of women were described as a fair, roughly symmetric curve with most around medium."
      note="Gold bar is a 50% reference line (what an even split would look like), not a second OkCupid headline statistic."
    >
      <svg
        className="study-chart__svg"
        viewBox="0 0 400 168"
        role="img"
        aria-label="Bar chart showing women rated about 80 percent of men below medium attractiveness, compared with a 50 percent even-split reference"
      >
        {rows.map((row, index) => {
          const y = 24 + index * 60
          const barWidth = (row.value / 100) * 250
          const lines = row.label.split('\n')
          return (
            <g key={row.label} opacity={row.muted ? 0.75 : 1}>
              {lines.map((line, lineIndex) => (
                <text key={line} x="0" y={y + 12 + lineIndex * 14} className="study-chart__label">
                  {line}
                </text>
              ))}
              <rect x="130" y={y} width="250" height="32" rx="4" className="study-chart__track" />
              <rect
                x="130"
                y={y}
                width={barWidth}
                height="32"
                rx="4"
                fill={row.color}
                opacity={row.muted ? 0.45 : 1}
              />
              <text x={138 + barWidth} y={y + 21} className="study-chart__value">
                {row.display}
              </text>
            </g>
          )
        })}
      </svg>
    </ChartShell>
  )
}

/** OkCupid: two-thirds of male messages to top third of women */
const MessageShareChart = () => {
  const top = 67
  const rest = 33

  return (
    <ChartShell
      title="Where men’s messages go (by women’s looks ranking)"
      source="Source: OkCupid / Rudder — “two-thirds of male messages go to the best-looking third of women.”"
      note="Rounded from OkCupid’s reported two-thirds → top third concentration."
    >
      <svg
        className="study-chart__svg"
        viewBox="0 0 400 150"
        role="img"
        aria-label="Stacked bar: about two thirds of men's messages go to the top third of women by looks; one third go to everyone else"
      >
        <rect x="40" y="36" width="320" height="44" rx="6" className="study-chart__track" />
        <rect x="40" y="36" width={(top / 100) * 320} height="44" rx="6" fill="var(--paper-flag)" />
        <rect
          x={40 + (top / 100) * 320}
          y="36"
          width={(rest / 100) * 320}
          height="44"
          rx="6"
          fill="var(--gold-deep)"
          opacity="0.55"
        />
        <text x="52" y="64" className="study-chart__on-bar">
          ~67% → top ⅓ of women
        </text>
        <text x="268" y="64" className="study-chart__on-bar study-chart__on-bar--dark">
          ~33% rest
        </text>
        <text x="40" y="112" className="study-chart__label">
          Top-rated third of women
        </text>
        <text x="220" y="112" className="study-chart__label">
          Everyone else
        </text>
        <rect x="40" y="120" width="14" height="14" rx="2" fill="var(--paper-flag)" />
        <rect
          x="220"
          y="120"
          width="14"
          height="14"
          rx="2"
          fill="var(--gold-deep)"
          opacity="0.55"
        />
      </svg>
    </ChartShell>
  )
}

/** Bruch & Newman: ~25% more desirable than self */
const AspirationalChart = () => {
  const self = 100
  const target = 125
  const max = 140

  return (
    <ChartShell
      title="Aspirational pursuit in online dating markets"
      source="Source: Bruch & Newman, Science Advances (2018) — partners contacted were on average about 25% more desirable than the sender, by the study’s measure."
      note="Illustrative scale: “self” at 100; typical contact target near 125. Same pattern reported for both men and women in four large U.S. cities."
    >
      <svg
        className="study-chart__svg"
        viewBox="0 0 400 150"
        role="img"
        aria-label="Bar chart comparing self desirability at 100 to typical contact target about 25 percent higher at 125"
      >
        {[
          { label: 'Sender’s desirability', value: self, color: 'var(--gold-deep)' },
          {
            label: 'Typical person they contact',
            value: target,
            color: 'var(--paper-flag)',
          },
        ].map((row, index) => {
          const y = 24 + index * 52
          const barWidth = (row.value / max) * 240
          return (
            <g key={row.label}>
              <text x="0" y={y + 16} className="study-chart__label">
                {row.label}
              </text>
              <rect x="140" y={y} width="240" height="30" rx="4" className="study-chart__track" />
              <rect x="140" y={y} width={barWidth} height="30" rx="4" fill={row.color} />
              <text x={148 + barWidth} y={y + 20} className="study-chart__value">
                {row.value}
              </text>
            </g>
          )
        })}
      </svg>
    </ChartShell>
  )
}
